/**
 * Single source for external market benchmarks.
 *
 * Rule: no figure is published anywhere on the site unless it carries a named
 * published source and the date it was retrieved. Until `source` and
 * `retrieved` are filled in, `cooLoadedCost.published` stays false and every
 * consumer renders the sourceless wording instead of a number.
 *
 * Do not invent a figure or a source. Fill this in from one named report.
 */

export type Benchmark = {
  /** Lower bound of the annual figure, in AUD. Null until sourced. */
  low: number | null;
  /** Upper bound of the annual figure, in AUD. Null until sourced. */
  high: number | null;
  /** Name of the published source, e.g. a named salary guide and its year. */
  source: string;
  /** URL of the published source. */
  sourceUrl: string;
  /** Date the figure was retrieved, ISO format. */
  retrieved: string;
  /** True only when a figure, a named source and a retrieval date all exist. */
  published: boolean;
};

/**
 * Loaded annual cost of a full-time COO in Australia, including
 * superannuation, incentives, recruitment and on-costs.
 *
 * AWAITING A SOURCE. Leave empty until one is supplied.
 */
export const cooLoadedCost: Benchmark = {
  low: null,
  high: null,
  source: "",
  sourceUrl: "",
  retrieved: "",
  published: false,
};

const aud = (n: number) => `$${n.toLocaleString("en-AU")}`;

/** The figure range, or an empty string while the benchmark is unsourced. */
export function cooLoadedCostRange(): string {
  if (!cooLoadedCost.published || cooLoadedCost.low === null) return "";
  if (cooLoadedCost.high === null) return aud(cooLoadedCost.low);
  return `${aud(cooLoadedCost.low)} to ${aud(cooLoadedCost.high)}`;
}

/** Attribution to print beside the figure. Empty while unsourced. */
export function cooLoadedCostAttribution(): string {
  if (!cooLoadedCost.published) return "";
  return `Source: ${cooLoadedCost.source}, retrieved ${cooLoadedCost.retrieved}.`;
}

/**
 * A full sentence about the cost of a full-time COO. While the benchmark is
 * unsourced this returns wording that carries no number.
 */
export function cooLoadedCostSentence(): string {
  return cooLoadedCost.published
    ? `A full-time COO in Australia costs ${cooLoadedCostRange()} a year fully loaded once superannuation, incentives, recruitment and on-costs are counted. ${cooLoadedCostAttribution()}`
    : "A full-time COO carries a loaded annual cost well above the salary line once superannuation, incentives, recruitment and on-costs are counted.";
}

/**
 * HTML block for the long-form articles. While the benchmark is unsourced this
 * returns prose with no figures in it, and no table.
 */
export function cooLoadedCostHtml(): string {
  if (!cooLoadedCost.published) {
    return `<p>${cooLoadedCostSentence()} Superannuation, an incentive, an amortised recruitment fee and workplace on-costs all sit on top of base salary, and the first year also carries the ramp-up period before the role is productive. We publish a figure here only when it can be attributed to a named salary guide.</p>`;
  }
  return `<p>The loaded annual cost of a capable full-time operator runs ${cooLoadedCostRange()} a year once superannuation, incentives, an amortised recruitment fee and workplace on-costs are counted. ${cooLoadedCostAttribution()}</p>`;
}

/**
 * Market monthly retainer band for a fractional COO in Australia, plus GST.
 * Both long-form pricing articles read from this one band rather than
 * publishing their own.
 *
 * AWAITING A SOURCE. Leave empty until one is supplied.
 */
export const fractionalRetainerBand: Benchmark = {
  low: null,
  high: null,
  source: "",
  sourceUrl: "",
  retrieved: "",
  published: false,
};

/** The monthly band, or an empty string while the benchmark is unsourced. */
export function fractionalRetainerRange(): string {
  if (!fractionalRetainerBand.published || fractionalRetainerBand.low === null) return "";
  if (fractionalRetainerBand.high === null) return aud(fractionalRetainerBand.low);
  return `${aud(fractionalRetainerBand.low)} to ${aud(fractionalRetainerBand.high)}`;
}

/** Attribution to print beside the band. Empty while unsourced. */
export function fractionalRetainerAttribution(): string {
  if (!fractionalRetainerBand.published) return "";
  return `Source: ${fractionalRetainerBand.source}, retrieved ${fractionalRetainerBand.retrieved}.`;
}

/**
 * A full sentence about market retainer pricing. While the band is unsourced
 * this returns wording that carries no number.
 */
export function fractionalRetainerSentence(): string {
  return fractionalRetainerBand.published
    ? `Monthly retainers in the Australian market run ${fractionalRetainerRange()} plus GST, set by the intensity of the engagement. ${fractionalRetainerAttribution()}`
    : "Monthly retainers are set by the intensity of the engagement, and most Australian practices quote on request rather than publishing a band.";
}

/**
 * Short label for a chart or a stat band. Carries no number while unsourced.
 */
export function fractionalRetainerLabel(): string {
  return fractionalRetainerBand.published
    ? `${fractionalRetainerRange()} / month retainer`
    : "monthly retainer, set by intensity";
}

/**
 * Short label for a chart or a stat band. Carries no number while unsourced.
 */
export function cooLoadedCostLabel(): string {
  return cooLoadedCost.published
    ? `${cooLoadedCostRange()} a year, all-in`
    : "loaded annual cost";
}
